import { Person, Reservation, ServiceBooking } from '@turizi/shared';
import { useState, useEffect } from 'react';
import { Button, LoadingOverlay, Popover } from '@mantine/core';
import { IconUsersGroup, IconMinus, IconPlus, IconCircleCheck, IconRowRemove, IconCrown, IconTrash, IconCopyPlusFilled, IconUserUp, IconUserX, IconUserStar, IconHeart, IconHeartFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import SearchInput from './search-input';


interface PaxSelectorProps {
    updatedPersons: Person[];
    persons: Person[];
    label?: string;
    onChange?: (persons: Person[]) => void;
    saving?: boolean;
    disabled?: boolean;
    size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    className?: string;
    showOnlyNumber?: boolean;
}

export default function PaxSelector({
    updatedPersons = [],
    persons = [],
    label = '',
    onChange,
    saving = false,
    disabled = false,
    size = 'xs',
    className = '',
    showOnlyNumber = false,
}: PaxSelectorProps) {
    const { t } = useTranslation();
    const [opened, setOpened] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [saved, setSaved] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Initialize selectedIds with current persons data
    useEffect(() => {
        if (updatedPersons) {
            setSelectedIds(updatedPersons.map(p => p.id));
        }
    }, [updatedPersons, persons]);


    useEffect(() => {
        if (saving && !saved) {
            setSaved(true);
            setTimeout(() => {
                setSaved(false);
                setOpened(false);
            }, 2000);
        }
    }, [saving]);

    const toggleSelection = (personId: number) => {
        if (disabled) return;
        setSelectedIds(prev =>
            prev.includes(personId)
                ? prev.filter(id => id !== personId)
                : [...prev, personId]
        );
    };

    const getTotalSelected = () => {
        return selectedIds.length;
    };

    const getSelectedPersonName = () => {
        if (selectedIds.length === 1) {
            const person = persons.find(p => p.id === selectedIds[0]);
            return person?.name || null;
        }
        return null;
    };

    const handleSave = async () => {
        const selectedPersons = persons.filter(p => selectedIds.includes(p.id));
        await onChange?.(selectedPersons);
        setOpened(false);
    };

    const hasChanges = () => {
        const originalIds = (updatedPersons || []).map(p => p.id);
        if (selectedIds.length !== originalIds.length) {
            return true;
        }
        const sortedSelected = [...selectedIds].sort();
        const sortedOriginal = [...originalIds].sort();
        return sortedSelected.some((id, idx) => id !== sortedOriginal[idx]);
    };

    const handleCancel = () => {
        setSelectedIds((updatedPersons || []).map(p => p.id));
        setOpened(false);
    };

    const handleOnlyThisOne = (person: Person) => {
        if (disabled) return;
        setSelectedIds([person.id]);
    };

    return (
        <Popover
            shadow="rgba(0, 0, 0, 0.4) 0px 8px 32px"
            radius="md"
            opened={opened}
            position='bottom-start'
            onChange={setOpened}
            closeOnEscape={true}
            clickOutsideEvents={['mousedown', 'touchstart']}
        >
            <Popover.Target>
                <div
                    className='w-fit break-all'
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpened(!opened);
                    }}
                >
                    {label && (
                        <label>{label}</label>
                    )}
                    <div
                        className={`
                            text-${size}
                            flex items-center justify-center gap-1 
                            ${className || 'solid-box-border-hover'}
                            ${opened ? 'solid-box-border-active' : ''} 
                            cursor-pointer
                        `}
                    >
                        <IconUsersGroup size={14} stroke={1} color="gray" />
                        <div className='flex items-center gap-1'>
                            {(() => {
                                const selectedName = getSelectedPersonName();
                                if (selectedName) {
                                    return (
                                        <>
                                            {!showOnlyNumber && (
                                                <>
                                                    <div className='text-gray-500'>{selectedName} </div>
                                                    <div className='text-gray-400 font-light'>x</div>
                                                </>
                                            )}
                                            <div className='font-bold'>1</div>
                                        </>
                                    );
                                }
                                return (
                                    <div className='flex items-center gap-1'>
                                        <div className='font-bold text-gray-400'>{t('paxs')}:</div>
                                        <div className='font-semibold'>{getTotalSelected()}</div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </Popover.Target>
            <Popover.Dropdown bg="#fcfbfa" p={0}>
                <div className="space-y-1 max-h-[600px] overflow-y-auto p-4">
                    <div className='mb-4'>
                        <SearchInput
                            size='sm'
                            onSearch={(value) => setSearchQuery(value)}
                        />
                    </div>
                    {persons.filter(person => person.name.toLowerCase().includes(searchQuery.toLowerCase())).map(person => {
                        const isSelected = selectedIds.includes(person.id);
                        return (
                            <div
                                key={person.id}
                                onClick={() => toggleSelection(person.id)}
                                className={`
                                    solid-box-border-hover-default flex items-center justify-between cursor-pointer py-2 px-3 rounded-lg border mb-1 transition-colors
                                    ${isSelected ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200 hover:border-gray-300'}
                                `}
                            >
                                <div className="flex flex-col">
                                    <div className="text-sm font-semibold text-gray-800">
                                        {person.name}
                                    </div>
                                    {(person.phone || person.email) && (
                                        <div className="text-xs text-gray-400 mt-0.5">
                                            {person.phone} {person.email && `· ${person.email}`}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                    {!disabled && (
                                        <Button
                                            size="xs"
                                            variant="subtle"
                                            color={isSelected ? 'red' : 'black'}
                                            onClick={() => handleOnlyThisOne(person)}
                                            title={t('select_only_this_one')}
                                        >
                                            {isSelected
                                                ? <IconHeartFilled size={16} stroke={2} />
                                                : <IconHeart size={16} stroke={2} />
                                            }
                                        </Button>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                </div>
                {!disabled && (
                    <div className="flex gap-4 justify-between backdrop-blur-xl p-4">
                        <Button
                            size="xs"
                            variant="subtle"
                            color='black'
                            onClick={handleCancel}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            size="xs"
                            color="black"
                            radius="xl"
                            onClick={handleSave}
                            loading={saving}
                            disabled={!hasChanges()}
                        >
                            {saved ? <IconCircleCheck size={20} /> : t('save')}
                        </Button>
                    </div>
                )}
            </Popover.Dropdown>
        </Popover >
    )

}
